import React, { useEffect, useMemo, useState } from "react";
import TreeView from "@mui/lab/TreeView";
import TreeItem from "@mui/lab/TreeItem";
import Checkbox from "@mui/material/Checkbox";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";

/**
 * PROPS:
 * - data: backend tree
 * - value: selected ids (string[])
 * - onChange: callback(selectedIds)
 */

export default function ProvinceTreeCheckbox({ data, value = [], onChange }) {
    const [checked, setChecked] = useState(value);

    useEffect(() => {
        setChecked(value);
    }, [value]);

    // 🔥 Normalize tree with UNIQUE IDs (IMPORTANT for MUI)
    const tree = useMemo(() => {
        return (data || []).map((country) => ({
            ...country,
            uid: `country-${country.id}`,
            children: (country.children || []).map((prov) => ({
                ...prov,
                uid: `province-${country.id}-${prov.id}`,
            })),
        }));
    }, [data]);

    // 🔥 get all descendants recursively
    const getDescendants = (node) => {
        let ids = [];

        const walk = (n) => {
            if (!n) return;
            ids.push(n.uid);
            (n.children || []).forEach(walk);
        };

        (node.children || []).forEach(walk);

        return ids;
    };

    // 🔥 check indeterminate state
    const isIndeterminate = (node) => {
        if (!node.children || node.children.length === 0) return false;

        const descendants = getDescendants(node);

        const some = descendants.some((id) => checked.includes(id));
        const all = descendants.every((id) => checked.includes(id));

        return some && !all;
    };

    // 🔥 toggle node (parent → children)
    const handleToggle = (node) => {
        const descendants = getDescendants(node);
        const allIds = [node.uid, ...descendants];

        const isChecked = checked.includes(node.uid);

        let newChecked;

        if (isChecked) {
            // remove node + children
            newChecked = checked.filter((id) => !allIds.includes(id));
        } else {
            // add node + children
            newChecked = [...new Set([...checked, ...allIds])];
        }

        setChecked(newChecked);
        onChange?.(newChecked);
    };

    // 🔥 render recursive tree
    const renderTree = (node) => (
        <TreeItem
            key={node.uid}
            nodeId={node.uid}
            label={
                <div style={{ display: "flex", alignItems: "center" }}>
                    <Checkbox
                        checked={checked.includes(node.uid)}
                        indeterminate={isIndeterminate(node)}
                        onClick={(e) => e.stopPropagation()}
                        onChange={() => handleToggle(node)}
                    />
                    {node.labelFr}
                </div>
            }
        >
            {(node.children || []).map(renderTree)}
        </TreeItem>
    );

    return (
        <TreeView
            defaultCollapseIcon={<ExpandMoreIcon />}
            defaultExpandIcon={<ChevronRightIcon />}
        >
            {tree.map(renderTree)}
        </TreeView>
    );
}